// Journey Lab live-control browser contract. This is a deliberate mirror of the server wire projection,
// because the browser and Node dashboard are built as separate bundles. Only safe control facts cross this
// boundary: credentials, addresses, message bodies, provider payloads and external record identifiers are
// absent from every type. Mutations are sent once and are never retried after an ambiguous response.

import { API_PREFIX, safeNote } from './post-intent.ts';
import type {
  JourneyLiveArmMode,
  JourneyLiveControlView,
  JourneyLivePreflightView,
  JourneyLiveRunReceiptView,
  JourneyLiveStatusView,
} from '../../../api/journey-wire.ts';

// WIRE_SSOT: app/modules/dashboard/api/journey-wire.ts
export type {
  JourneyLiveArmIntent,
  JourneyLiveArmMode,
  JourneyLiveBudgetCapsView,
  JourneyLiveControlState,
  JourneyLiveControlView,
  JourneyLiveInputReadinessView,
  JourneyLivePreflightCheckView,
  JourneyLivePreflightIntent,
  JourneyLivePreflightView,
  JourneyLiveRunReceiptView,
  JourneyLiveStartIntent,
  JourneyLiveStatusView,
  JourneyLiveStopIntent,
} from '../../../api/journey-wire.ts';

export interface JourneyLiveApi {
  readonly getStatus: () => Promise<JourneyLiveStatusView>;
  readonly preflight: (mode: JourneyLiveArmMode) => Promise<JourneyLivePreflightView>;
  readonly arm: (mode: JourneyLiveArmMode, preflightId: string) => Promise<JourneyLiveControlView>;
  readonly start: (
    scenarioId: string,
    mode: JourneyLiveArmMode,
    controlGeneration: number,
    preflightId: string,
  ) => Promise<JourneyLiveRunReceiptView>;
  readonly stop: (reason: string) => Promise<JourneyLiveControlView>;
}

const LIVE_PATH = `${API_PREFIX}/journeys/live`;
const liveRunPath = (scenarioId: string): string =>
  `${API_PREFIX}/journeys/${encodeURIComponent(scenarioId)}/live-run`;

async function readLiveJson<T>(path: string): Promise<T> {
  const response = await fetch(path, {
    method: 'GET',
    headers: { accept: 'application/json' },
    credentials: 'same-origin',
    cache: 'no-store',
  });
  if (!response.ok) throw new Error(await safeNote(response));
  return (await response.json()) as T;
}

async function postLiveJson<T>(
  path: string,
  body: Readonly<Record<string, string | number>>,
): Promise<T> {
  const response = await fetch(path, {
    method: 'POST',
    headers: { 'content-type': 'application/json', accept: 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(body),
  });
  if (!response.ok) throw new Error(await safeNote(response));
  return (await response.json()) as T;
}

/** The authenticated, same-origin live-control transport. No call retries a control-plane mutation. */
export function httpJourneyLiveApi(): JourneyLiveApi {
  return {
    getStatus: () => readLiveJson<JourneyLiveStatusView>(LIVE_PATH),
    preflight: (mode) => postLiveJson<JourneyLivePreflightView>(`${LIVE_PATH}/preflight`, { mode }),
    arm: (mode, preflightId) =>
      postLiveJson<JourneyLiveControlView>(`${LIVE_PATH}/arm`, { mode, preflightId }),
    start: (scenarioId, mode, controlGeneration, preflightId) =>
      postLiveJson<JourneyLiveRunReceiptView>(liveRunPath(scenarioId), {
        mode,
        controlGeneration,
        preflightId,
      }),
    stop: (reason) => postLiveJson<JourneyLiveControlView>(`${LIVE_PATH}/stop`, { reason }),
  };
}
